import { computed, type MaybeRef, unref } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";
import type { AdminWalletTransaction, AdminWalletTransactionsQueryParams } from "@shopexperts/legacy-api";

interface UseAdminWalletTransactionsOptions {
  filters?: MaybeRef<AdminWalletTransactionsQueryParams | undefined>;
  perPage?: number;
  enabled?: MaybeRef<boolean>;
}

export function useAdminWalletTransactions(options: UseAdminWalletTransactionsOptions = {}) {
  const api = useApi();
  const perPage = options.perPage ?? 20;
  const enabled = computed(() => unref(options.enabled) ?? true);
  const normalizedFilters = computed<AdminWalletTransactionsQueryParams>(() => ({
    ...(unref(options.filters) ?? {}),
    per_page: perPage,
  }));

  const query = useInfiniteQuery({
    queryKey: computed(() => ["admin", "wallet", "transactions", normalizedFilters.value]),
    queryFn: async ({ pageParam }) => {
      return await api.admin.wallet.listWalletTransactions({
        query: {
          ...normalizedFilters.value,
          cursor: pageParam,
        },
      });
    },
    getNextPageParam: (lastPage) => lastPage.pagination.has_next ? lastPage.pagination.cursor : undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  const transactions = computed<AdminWalletTransaction[]>(() =>
    query.data.value?.pages.flatMap((page) => page.data) ?? [],
  );

  return {
    transactions,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    query,
  };
}
